"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquareQuote, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "What is an SEO agency alternative?",
  answer: "An SEO agency alternative means partnering directly with a dedicated, senior independent consultant rather than going through a multi-layered agency team. Instead of communicating through account managers and having your work passed down to junior staff, you work directly with me. Every strategy, audit, and technical implementation is crafted and executed personally with direct accountability."
}, {
  id: "item-2",
  question: "SEO consultant vs SEO agency — what's the real difference?",
  answer: <>
        The core difference lies in team structure and communication. With an independent consultant, a single senior specialist leads strategy and execution personally with no account management layers or handover friction. An agency provides a multi-person team across various disciplines, which can dilute direct technical focus. Neither model is universally better—it depends on whether you want agile, bespoke support through retained{" "}
        <Link to="/MonthlySeoServicesPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
          monthly SEO services
        </Link>{" "}
        or a multi-department agency setup.
      </>
}, {
  id: "item-3",
  question: "When is an independent SEO consultant the better choice?",
  answer: <>
        An independent SEO consultant is ideal for SMEs, ecommerce brands, local businesses, and B2B companies wanting focused search expertise, direct communication, and flexible engagement terms. It is the best fit when you want high-level execution and practical,{" "}
        <Link to="/AffordableSeoServicesUkPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
          affordable SEO services in the UK
        </Link>{" "}
        without paying for large agency overheads, account managers, and pitch decks.
      </>
}, {
  id: "item-4",
  question: "When might a full SEO agency still be the right fit?",
  answer: "A full marketing agency may still be the right fit if your business requires an integrated multi-channel campaign spanning paid search (PPC), digital PR, organic social media, brand design, and full web development under one roof, or if you need large-scale outsourced production capacity across several marketing verticals at once."
}, {
  id: "item-5",
  question: "Can a freelance SEO consultant manage a complete SEO strategy?",
  answer: "Yes. I oversee the end-to-end SEO lifecycle personally. This covers in-depth technical audits, site architecture improvements, commercial keyword research, content briefs, digital PR direction, on-page optimization, and regular performance reporting tailored to your internal stakeholders and business goals."
}, {
  id: "item-6",
  question: "How much does an independent SEO consultant cost?",
  answer: "My pricing is transparent and based on scope, technical complexity, and the required monthly hours. Because I operate independently without large agency overheads, your entire investment goes directly into senior hands-on work and measurable progress rather than office perks or account management fees."
}, {
  id: "item-7",
  question: "Can an SEO consultant work alongside an existing in-house marketing team?",
  answer: "Yes, collaboration is seamless. I frequently work alongside internal marketing managers, in-house content writers, and development teams. I provide clear technical specifications, strategic direction, and implementation reviews to ensure your existing team executes SEO best practices effectively."
}, {
  id: "item-8",
  question: "Can I switch from an SEO agency to an independent consultant without losing rankings?",
  answer: "Yes. A seamless transition begins with a structured handover and an initial technical audit to establish what is currently driving traffic, diagnose any broken elements or missed opportunities, and preserve what is working. By protecting your established ranking signals while fixing underlying issues, we ensure your search visibility remains protected."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faq" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {/* Subtle decorative background glow */}
      <div className="pointer-events-none absolute top-1/3 -left-48 h-96 w-96 rounded-full bg-secondary/50 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-10 -right-48 h-96 w-96 rounded-full bg-muted blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl relative z-10">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide">
            <HelpCircle className="size-3.5 mr-1 text-primary" />
            Clarity & Expectations
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Frequently asked questions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Everything you need to know about working directly with me as your dedicated freelance SEO consultant.
          </p>
        </motion.div>

        {/* Accordion Box */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="bg-card border border-border rounded-xl p-4 sm:p-8 md:p-10 shadow-sm">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqs.map((faq, index) => <div key={faq.id} data-index={index}>
                <AccordionItem value={faq.id} className="border border-border/80 rounded-lg px-4 sm:px-6 transition-[border-color,background-color] data-[state=open]:border-primary/40 data-[state=open]:bg-background/80">
                  <AccordionTrigger className="text-base sm:text-lg font-semibold text-foreground py-5 hover:no-underline hover:text-primary transition-colors">
                    <span className="text-left font-default pr-2">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <p className="flex-1">{faq.answer}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        {/* Bottom Consultation CTA Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-12 sm:mt-16 rounded-xl bg-secondary/60 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="flex flex-col sm:flex-row items-center md:items-start gap-4">
            <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <MessageSquareQuote className="size-6" />
            </div>
            <div>
              <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
                Have a specific question about your website?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Let&apos;s talk directly about your search performance and growth opportunities.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild variant="outline" size="sm" className="bg-background">
              <Link to="/ContactPage">Contact me</Link>
            </Button>
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                Book a free consultation
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}